/**
 * FLX bespoke landing — the asset slot the client's photography drops into.
 * Presentation-only (local UI state, no data).
 */
import { type LucideIcon, ImageIcon } from 'lucide-react';
import { useState } from 'react';
import { FONT_DISPLAY } from './tokens';

/**
 * An image the CLIENT supplies.
 *
 * The redesign hotlinked twelve assets from the client's own marketing site; the
 * repo carries none of them yet. So this renders the real file when it loads and
 * a styled placeholder frame when it 404s — the page composes correctly today and
 * fills in the moment the files land in `public/tenants/flx/`, with no code change.
 * A broken-image glyph on a launch page would be worse than an honest frame.
 *
 * Presentation (size, object-fit, hover transforms) is owned by `flx.css` via
 * `className` — inline styles can't express `:hover`.
 */
export function ImageSlot({
  src,
  label,
  Icon = ImageIcon,
  className,
}: {
  src: string | null;
  label: string;
  Icon?: LucideIcon;
  className?: string;
}) {
  const [failed, setFailed] = useState(false);
  if (src && !failed) {
    return (
      <img
        src={src}
        alt={label}
        className={className}
        loading="lazy"
        onError={() => setFailed(true)}
      />
    );
  }
  return (
    <div
      className={className}
      style={{
        width: '100%',
        height: '100%',
        minHeight: 120,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        padding: 24,
        textAlign: 'center',
        color: 'var(--mute)',
        border: '1px dashed var(--line-strong)',
      }}
    >
      <Icon size={30} strokeWidth={1.4} style={{ opacity: 0.5 }} />
      <span
        style={{
          fontFamily: FONT_DISPLAY,
          fontSize: 11,
          letterSpacing: '.14em',
          textTransform: 'uppercase',
          opacity: 0.7,
        }}
      >
        {label}
      </span>
    </div>
  );
}
